import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas";

/**
 * Reading one workspace file, whole, for someone looking at it on a phone.
 *
 * Forge already moves file contents over `workspace.readFile`, but that is the
 * bulk-transfer protocol: a stream of chunks with offsets, checksums and a
 * resumable transfer id, built for moving large files reliably. A file viewer
 * wants none of that. It wants the text, once, with a note if it was cut short.
 *
 * So this is a second route rather than a rename. The mobile file viewer called
 * `projects.readFile` and Forge served nothing by that name, which is why
 * tapping any file in the Files browser read "File unavailable". The tree it
 * listed came from the project snapshot, so the browser looked healthy right up
 * until you touched something.
 *
 * The shapes mirror `@t3tools/contracts` exactly, deliberately. That package is
 * the vendored client and does not import this one, so the only thing keeping
 * them in agreement is that they were written to match and a parity test that
 * fails when they stop matching.
 */

const PROJECT_READ_FILE_PATH_MAX_LENGTH = 512;

export const ProjectReadFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_READ_FILE_PATH_MAX_LENGTH)),
});
export type ProjectReadFileInput = typeof ProjectReadFileInput.Type;

/**
 * `truncated` is the whole reason `byteLength` is here too: a viewer that shows
 * the first slice of a large file has to say so, and it can only say how much
 * it is missing if it is told the real size.
 */
export const ProjectReadFileResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  contents: Schema.String,
  byteLength: NonNegativeInt,
  truncated: Schema.Boolean,
});
export type ProjectReadFileResult = typeof ProjectReadFileResult.Type;

/**
 * Why a read did not happen, in the vocabulary the client already speaks.
 *
 * The two `outside_root` members are the security cases and are kept apart on
 * purpose. `workspace_path_outside_root` is a path that never belonged, such as
 * `../../etc/passwd`. `resolved_path_outside_root` is a path that looked fine
 * and pointed somewhere else once symlinks were followed. Collapsing them would
 * hide which of the two checks did the work.
 */
export const ProjectFileFailure = Schema.Literals([
  "workspace_path_outside_root",
  "resolved_path_outside_root",
  "path_not_file",
  "binary_file",
  "operation_failed",
]);
export type ProjectFileFailure = typeof ProjectFileFailure.Type;

/** Which syscall failed, so `operation_failed` is not the end of the story. */
export const ProjectFileOperation = Schema.Literals([
  "realpath-workspace-root",
  "realpath-target",
  "open",
  "stat",
  "read",
  "close",
  "make-directory",
  "write-file",
]);
export type ProjectFileOperation = typeof ProjectFileOperation.Type;

export type ProjectFileFailureContext = {
  readonly cwd: string;
  readonly relativePath: string;
  readonly failure: ProjectFileFailure;
  readonly resolvedPath?: string;
  readonly resolvedWorkspaceRoot?: string;
  readonly operation?: ProjectFileOperation;
  readonly operationPath?: string;
  readonly cause?: unknown;
};

function decodedProjectErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

export class ProjectReadFileError extends Schema.TaggedErrorClass<ProjectReadFileError>()(
  "ProjectReadFileError",
  {
    cwd: Schema.optional(TrimmedNonEmptyString),
    relativePath: Schema.optional(TrimmedNonEmptyString),
    failure: Schema.optional(ProjectFileFailure),
    resolvedPath: Schema.optional(TrimmedNonEmptyString),
    resolvedWorkspaceRoot: Schema.optional(TrimmedNonEmptyString),
    operation: Schema.optional(ProjectFileOperation),
    operationPath: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // Structured fields stay optional on the wire so a newer peer can still decode
  // a legacy message-only failure. New code goes through this constructor.
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: ProjectFileFailureContext) {
    super({
      ...props,
      message:
        decodedProjectErrorMessage(props) ??
        `Failed to read workspace file '${props.relativePath}' in '${props.cwd}'.`,
    } as any);
  }
}
